import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { ArrowLeft, Plus, Trash2, Home, MapPin, ClipboardCheck, GitCompareArrows } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { ProTag, useHasPro } from '../components/ProUpgrade';

import { API } from '../lib/api';

const roomTypes = ['bedroom', 'bathroom', 'kitchen', 'living_room', 'dining_room', 'hallway', 'garage', 'basement', 'attic', 'closet', 'office', 'laundry', 'other'];

export default function PropertyDetailPage() {
  const { id } = useParams();
  const hasPro = useHasPro();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [roomDialog, setRoomDialog] = useState(false);
  const [roomForm, setRoomForm] = useState({ name: '', room_type: 'bedroom' });
  const [addingRoom, setAddingRoom] = useState(false);
  const [inspDialog, setInspDialog] = useState(false);
  const [inspType, setInspType] = useState('move_out');
  const [startingInsp, setStartingInsp] = useState(false);
  const [inspections, setInspections] = useState([]);

  const fetchProperty = async () => {
    try {
      const [propRes, inspRes] = await Promise.all([
        axios.get(`${API}/properties/${id}`),
        axios.get(`${API}/inspections`),
      ]);
      setProperty(propRes.data);
      setInspections(inspRes.data.filter((i) => i.property_id === id));
    } catch {
      toast.error('Property not found');
      navigate('/properties');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchProperty(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const addRoom = async (e) => {
    e.preventDefault();
    setAddingRoom(true);
    try {
      await axios.post(`${API}/properties/${id}/rooms`, roomForm);
      toast.success('Room added');
      setRoomDialog(false);
      setRoomForm({ name: '', room_type: 'bedroom' });
      fetchProperty();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add room');
    } finally {
      setAddingRoom(false);
    }
  };

  const deleteRoom = async (roomId) => {
    try {
      await axios.delete(`${API}/properties/${id}/rooms/${roomId}`);
      toast.success('Room removed');
      fetchProperty();
    } catch {
      toast.error('Failed to remove room');
    }
  };

  const startInspection = async () => {
    setStartingInsp(true);
    try {
      const res = await axios.post(`${API}/inspections`, { property_id: id, inspection_type: inspType });
      toast.success(`${inspType === 'move_in' ? 'Move-in' : 'Move-out'} inspection started`);
      setInspDialog(false);
      navigate(`/inspections/${res.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start inspection');
    } finally {
      setStartingInsp(false);
    }
  };

  const hasMoveIn = inspections.some((i) => i.inspection_type === 'move_in');
  const hasMoveOut = inspections.some((i) => i.inspection_type === 'move_out');
  const canCompare = hasMoveIn && hasMoveOut;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-sunken rounded w-32" />
          <div className="h-32 bg-sunken rounded-md border border-line" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div data-testid="property-detail-page">
        <Link to="/properties" className="inline-flex items-center gap-2 text-ink-muted hover:text-ink mb-6 transition-colors text-sm" data-testid="back-to-properties">
          <ArrowLeft className="w-4 h-4" /> Properties
        </Link>

        {/* Property Header */}
        <div className="border border-line rounded-md bg-surface p-6 mb-6">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-md bg-sunken flex items-center justify-center">
                <Home className="w-6 h-6 text-ink" strokeWidth={1.5} />
              </div>
              <div>
                <h1 className="text-xl font-semibold text-ink font-display" data-testid="property-name">{property.name}</h1>
                <div className="flex items-center gap-1 mt-1">
                  <MapPin className="w-3 h-3 text-ink-muted" />
                  <span className="text-sm text-ink-muted">{property.address}</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {canCompare && (
                <Link to={`/properties/${id}/compare`}>
                  <Button variant="outline" className="border-line-strong text-ink" data-testid="compare-turnover-btn">
                    <GitCompareArrows className="w-4 h-4 mr-2" />
                    Compare move-in and move-out
                    {!hasPro && <ProTag className="ml-1" />}
                  </Button>
                </Link>
              )}
              <Dialog open={inspDialog} onOpenChange={setInspDialog}>
                <DialogTrigger asChild>
                  <Button
                    className="bg-marker hover:bg-marker-hover text-on-marker"
                    data-testid="start-inspection-btn"
                  >
                    <ClipboardCheck className="w-4 h-4 mr-2" />
                    Start Inspection
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-surface border-line text-ink max-w-sm">
                  <DialogHeader>
                    <DialogTitle className="text-ink font-display">Start Inspection</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label className="text-ink">Inspection Type</Label>
                      <Select value={inspType} onValueChange={setInspType}>
                        <SelectTrigger className="bg-sunken border-line h-11" data-testid="insp-type-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="move_in">Move-In (baseline)</SelectItem>
                          <SelectItem value="move_out">Move-Out (final)</SelectItem>
                          <SelectItem value="routine">Routine</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button
                      onClick={startInspection}
                      disabled={startingInsp}
                      className="w-full bg-marker hover:bg-marker-hover text-on-marker h-11"
                      data-testid="start-insp-submit"
                    >
                      {startingInsp ? 'Starting...' : 'Start'}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>
          <div className="grid grid-cols-4 gap-4 mt-6">
            <div className="bg-sunken border border-line rounded-md p-3">
              <p className="text-xs text-ink-muted uppercase tracking-[0.08em]">Type</p>
              <p className="text-sm font-medium text-ink capitalize mt-1">{property.property_type}</p>
            </div>
            <div className="bg-sunken border border-line rounded-md p-3">
              <p className="text-xs text-ink-muted uppercase tracking-[0.08em]">Units</p>
              <p className="text-sm font-medium text-ink mt-1">{property.unit_count}</p>
            </div>
            <div className="bg-sunken border border-line rounded-md p-3">
              <p className="text-xs text-ink-muted uppercase tracking-[0.08em]">Rooms</p>
              <p className="text-sm font-medium text-ink mt-1">{property.rooms?.length || 0}</p>
            </div>
            <div className="bg-sunken border border-line rounded-md p-3">
              <p className="text-xs text-ink-muted uppercase tracking-[0.08em]">Damage Score</p>
              <p className={`text-sm font-bold font-mono mt-1 ${
                property.avg_damage_score === null ? 'text-ink-muted':
                property.avg_damage_score <= 40 ? 'text-sev-low':
                property.avg_damage_score <= 60 ? 'text-sev-med': 'text-sev-high'
              }`}>
                {property.avg_damage_score !== null ? property.avg_damage_score : 'N/A'}
              </p>
            </div>
          </div>
        </div>

        {/* Rooms */}
        <div className="border border-line rounded-md overflow-hidden bg-surface" data-testid="rooms-section">
          <div className="bg-sunken px-5 py-4 border-b border-line flex items-center justify-between">
            <h2 className="text-sm font-semibold text-ink">Rooms ({property.rooms?.length || 0})</h2>
            <Dialog open={roomDialog} onOpenChange={setRoomDialog}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" className="border-line-strong text-ink hover:bg-sunken hover:text-ink text-xs" data-testid="add-room-btn">
                  <Plus className="w-3 h-3 mr-1" /> Add Room
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-surface border-line text-ink max-w-sm">
                <DialogHeader>
                  <DialogTitle className="text-ink font-display">Add Room</DialogTitle>
                </DialogHeader>
                <form onSubmit={addRoom} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label className="text-ink">Room Name</Label>
                    <Input
                      data-testid="room-name-input"
                      value={roomForm.name}
                      onChange={(e) => setRoomForm({ ...roomForm, name: e.target.value })}
                      placeholder="Master Bedroom"
                      className="bg-sunken border-line text-ink placeholder:text-ink-muted h-11"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-ink">Type</Label>
                    <Select value={roomForm.room_type} onValueChange={(v) => setRoomForm({ ...roomForm, room_type: v })}>
                      <SelectTrigger data-testid="room-type-select" className="bg-sunken border-line text-ink h-11">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-surface border-line">
                        {roomTypes.map(rt => (
                          <SelectItem key={rt} value={rt}>{rt.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button data-testid="add-room-submit" type="submit" disabled={addingRoom} className="w-full bg-marker hover:bg-marker-hover text-on-marker h-11">
                    {addingRoom ? 'Adding...' : 'Add Room'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {(!property.rooms || property.rooms.length === 0) ? (
            <div className="bg-surface p-8 text-center">
              <p className="text-sm text-ink-muted">No rooms defined. Add rooms before starting an inspection.</p>
            </div>
          ) : (
            <div className="divide-y divide-line">
              {property.rooms.map((room) => (
                <div key={room.id} className="flex items-center justify-between px-5 py-3 bg-surface hover:bg-sunken transition-colors" data-testid={`room-${room.id}`}>
                  <div>
                    <p className="text-sm font-medium text-ink">{room.name}</p>
                    <p className="text-xs text-ink-muted capitalize">{room.room_type.replace('_', ' ')}</p>
                  </div>
                  <button onClick={() => deleteRoom(room.id)} className="text-ink-muted hover:text-sev-high transition-colors p-1" data-testid={`delete-room-${room.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
